import {
    afterNextRender,
    inject,
    Injectable,
    makeStateKey,
    PLATFORM_ID,
    TransferState,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { PageResolverResopnse } from '../types/page-resolver-response.type';
import { Property } from '../../common/types/property.type';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { REQUEST } from '../../../express.token';
import { DatePipe, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { BehaviorSubject, filter } from 'rxjs';
import { JsendResponse } from '../../common/types/jsend.response.type';
import { GlobalService } from '../../common/services/global.service';
import { CommonHelper } from '../../common/helpers/commonHelper';

/**
 * Carries the heading SSR already resolved over to the browser, so hydration can
 * render the same text instead of briefly falling back to the token-free version.
 */
const PAGE_HEADING_KEY = makeStateKey<{ template: string; heading: string }>(
    'listing-page-heading',
);

@Injectable({ providedIn: 'root' })
export class MetaService {
    private titleService = inject(Title);
    private metaService = inject(Meta);
    private expressReq = inject(REQUEST, { optional: true });
    private platformId = inject(PLATFORM_ID);
    private transferState = inject(TransferState);

    /**
     * Heading SSR already resolved, handed over so the browser can recognise it has
     * this page's heading and leave it alone during hydration.
     */
    private transferredHeading = this.transferState.get(PAGE_HEADING_KEY, {
        template: '',
        heading: '',
    });
    private datePipe = inject(DatePipe);
    private globalService = inject(GlobalService);
    private router = inject(Router);

    fallbackImage = this.globalService.branding?.value?.fallback_image || '';

    // True on the server (no hydration there). On the browser, flipped to
    // true after the first render — i.e., once hydration is complete. Used
    // by `updateDescription` to skip the client-side resolver re-run that
    // would otherwise overwrite SSR's correct description with the raw
    // `{COUNT}`/`{AVG_PRICE}` template (a window crawlers can snapshot).
    private hydrationComplete = !isPlatformBrowser(this.platformId);

    /**
     * Raw meta title / description templates as returned by the page API.
     * These may contain {COUNT}/{AVG_PRICE} placeholders that get replaced
     * with real values once the stats API response arrives. They are kept in
     * memory so the stats components can resolve the templates later,
     * independent of what is currently written to the DOM.
     */
    private metaTitleTemplate = '';
    private metaDescriptionTemplate = '';

    /**
     * The page heading (H1) template, e.g. `Homes in {CITY} - {COUNT} listings`.
     * Kept here beside the meta templates so `{COUNT}`/`{AVG_PRICE}` resolve for the
     * heading in the same pass -- those two values only exist once the stats response
     * lands, so the API cannot fill them in.
     */
    private pageHeadingTemplate = this.transferredHeading.template;

    /**
     * Heading the layout renders. Emits the token-free text first and the resolved
     * text once stats arrive, so a visitor (or a crawler) never sees a raw `{COUNT}`.
     * Seeded from the SSR-resolved value on the browser so hydration paints the same
     * heading the server sent.
     */
    public pageHeading: BehaviorSubject<string> = new BehaviorSubject<string>(
        this.transferredHeading.heading,
    );

    constructor() {
        afterNextRender(() => {
            this.hydrationComplete = true;
        });

        if (isPlatformBrowser(this.platformId)) {
            this.router.events
                .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
                .subscribe(() => this.refreshCanonicalForQueryChange());
        }
    }

    /**
     *
     * @returns
     */
    updateMetaTags(response: PageResolverResopnse | null, route?: ActivatedRouteSnapshot): void {
        if (!response) {
            return;
        }

        const { propertyDetailsResponse, pageDataResponse } = response;

        // Adding noindex meta tag to properties that become closed or expired before new Date(Date.UTC(2025, 1, 27));
        // if (pageDataResponse?.body?.data?.page_type === 'closed-properties-page') {
        //     this.metaService.addTag({ name: 'robots', content: 'noindex' });
        // } else {
        //     // Remove meta robots tag
        //     this.metaService.removeTag('name="robots"');
        // }

        if (propertyDetailsResponse) {
            const property: Property = propertyDetailsResponse?.body?.data.property;
            const path = this.pathWithPageParam(route!);
            const propertyMetaTags = this.getPropertyDetailsMetaTags(property);

            this.updateTitle(propertyMetaTags.meta_title);
            const keywords = `${CommonHelper.fullAddress(property)} - ${this.globalService.companyDetails.value?.name}`;
            this.updateKeywords(keywords);
            this.updateDescription(propertyMetaTags.meta_description);
            this.ogTagAddDescription();
            this.metaService.addTag({ property: 'og:type', content: 'website' });
            this.ogTagAddUrl(path);
            this.ogTagAddTitle(this.titleService.getTitle());
            if (property.media?.length) {
                const ogImage = property.media?.[0]?.optimized_images?.medium_url || property.media?.[0]?.media_url
                this.ogTagAddImage(ogImage);
            } else if (this.globalService.branding?.value?.og_fallback_image) {
                this.ogTagAddImage(this.globalService.branding?.value?.og_fallback_image);
            }
            this.updateCanonicalUrl(path);
            this.updateSchemaMarkup(property, path);
            return;
        }

        if (pageDataResponse) {
            const { meta_title, meta_description, meta_keywords, title, og_image } =
                pageDataResponse?.body?.data.page;
            const path = this.pathWithPageParam(route!);
            this.updateTitle(
                meta_title || `${title} | ${this.globalService.companyDetails.value?.name}`,
            );
            this.updateDescription(
                meta_description || `${title} | ${this.globalService.companyDetails.value?.name}`,
            );
            this.updateKeywords(meta_keywords);
            this.ogTagAddTitle(this.titleService.getTitle());
            this.ogTagAddDescription();
            this.metaService.addTag({ property: 'og:type', content: 'website' });
            this.ogTagAddUrl(path);
            if (og_image) {
                this.ogTagAddImage(og_image);
            } else if (this.globalService.branding?.value?.og_fallback_image) {
                this.ogTagAddImage(this.globalService.branding?.value?.og_fallback_image);
            }
            this.updateCanonicalUrl(path);
        }
    }

    /**
     * Builds property detail SEO tags from the admin-configured templates.
     * Defaults are hydrated into propertyDetailsConfig by the resolver when no saved admin template exists.
     */
    private getPropertyDetailsMetaTags(property: Property): { meta_title: string; meta_description: string } {
        const templateSettings = this.globalService.propertyDetailsConfig.value?.meta_tags_template;
        let metaTitleTemplate = templateSettings?.meta_title || '';
        const metaDescriptionTemplate = templateSettings?.meta_description || '';
        const placeholders = this.getPropertyDetailsMetaPlaceholders(property);

        // For closed / off-market listings the MLS number is no longer useful in
        // the public title — drop the {MLS_NUMBER} token (and its "MLS #" label)
        // from the template before it is filled in, so the real address/company
        // values are never touched.
        if (this.isClosedOrOffMarket(property)) {
            metaTitleTemplate = this.stripMlsFromTemplate(metaTitleTemplate);
        }

        return {
            meta_title: this.resolveTemplate(metaTitleTemplate, placeholders),
            meta_description: this.resolveTemplate(metaDescriptionTemplate, placeholders),
        };
    }

    /**
     * True when a property is closed or otherwise off-market (no longer for sale).
     */
    private isClosedOrOffMarket(property: Property): boolean {
        const status = (property.standard_status || '').toLowerCase();
        return ['closed', 'expired', 'withdrawn', 'canceled', 'cancelled', 'hold', 'off market', 'off-market'].includes(status);
    }

    /**
     * Removes the MLS from a meta-title template for closed / off-market listings.
     *
     * The default template is `{ADDRESS} | MLS# {MLS_NUMBER} | {COMPANY}` — i.e.
     * "|"-delimited segments. We strip the "MLS #" label + {MLS_NUMBER} token from
     * each segment, then drop any segment left empty (the dedicated MLS segment).
     * Operating on the template — not the resolved title — means the real
     * ADDRESS/COMPANY/PRICE values are never touched.
     */
    private stripMlsFromTemplate(template: string): string {
        if (!template) return template;
        return template
            .split('|')
            .map((segment) =>
                segment
                    // "MLS# {MLS_NUMBER}" / "MLS #{MLS_NUMBER}" / "MLS {MLS_NUMBER}" / bare "{MLS_NUMBER}"
                    .replace(/(?:MLS\s*#?\s*)?\{MLS_NUMBER\}/gi, '')
                    // brackets left empty after removal, e.g. "(MLS# {MLS_NUMBER})" -> "()"
                    .replace(/\(\s*\)|\[\s*\]/g, '')
                    .replace(/\s{2,}/g, ' ')
                    .trim(),
            )
            .filter(Boolean)
            .join(' | ')
            .trim();
    }

    /**
     * Maps supported template placeholders to the current property's real values.
     */
    private getPropertyDetailsMetaPlaceholders(property: Property): Record<string, string> {
        return {
            ADDRESS: CommonHelper.fullAddress(property),
            CITY: property.city || '',
            STATE: property.state_code || '',
            ZIP: property.postal_code || '',
            COMPANY: this.globalService.companyDetails.value?.name || '',
            PRICE: property.list_price != null ? `$${Number(property.list_price).toLocaleString()}` : '',
            MLS_NUMBER: property.formatted_listing_id || property.listing_id || '',
            BEDROOMS: property.bedrooms_total != null ? property.bedrooms_total.toString() : '',
            BATHROOMS: property.bathrooms_total != null ? property.bathrooms_total.toString() : '',
            SQFT: property.living_area != null ? Number(property.living_area).toLocaleString() : '',
            PHOTO_COUNT: (property.photos_count ?? property.media?.length ?? 0).toString(),
            LISTING_DATE: property.listing_date
                ? this.datePipe.transform(new Date(property.listing_date), 'MM-dd-yyyy') || ''
                : '',
            PROPERTY_TYPE: property.property_type || '',
        };
    }

    /**
     * Replaces tokens such as {ADDRESS} or {MLS_NUMBER} in a template string.
     */
    private resolveTemplate(template: string, placeholders: Record<string, string>): string {
        let result = template;
        Object.entries(placeholders).forEach(([key, value]) => {
            result = result.replace(new RegExp(`\\{${key}\\}`, 'gi'), value || '');
        });
        return result;
    }

    updateStaticPageMetaTags(
        title: string,
        meta_description: string,
        path: string,
        image?: string,
    ) {
        this.updateTitle(title);
        // this.updateKeywords(keywords);
        this.metaService.removeTag('name="robots"');
        this.updateDescription(meta_description);
        this.ogTagAddDescription();
        this.metaService.addTag({ property: 'og:type', content: 'website' });
        this.ogTagAddUrl(path);
        this.ogTagAddTitle(this.titleService.getTitle());
        if (image) {
            this.ogTagAddImage(image);
        } else if (this.globalService.branding?.value?.og_fallback_image) {
            this.ogTagAddImage(this.globalService.branding?.value?.og_fallback_image);
        }
        this.updateCanonicalUrl(path);
    }

    updateHomePageMetaTags(response: any) {
        this.metaService.removeTag('name="robots"');
        this.updateTitle(response.meta_title);
        this.updateDescription(response.meta_description);
        // this.updateKeywords(response.meta_keywords);
        this.ogTagAddDescription();
        this.ogTagAddTitle(response.meta_title);
        this.metaService.addTag({ property: 'og:type', content: 'website' });
        this.ogTagAddUrl(response.url);
        if (this.globalService.branding?.value?.og_fallback_image) {
            this.ogTagAddImage(this.globalService.branding?.value?.og_fallback_image);
        }
        this.updateCanonicalUrl(response.url);
    }

    updateBlogPageMetaTags(response: any) {
        this.metaService.removeTag('name="robots"');
        this.updateTitle(response.title);
        this.updateDescription(response.meta_description);
        // this.updateKeywords(response.meta_keywords);
        this.ogTagAddDescription();
        this.ogTagAddTitle(response.og_title);
        this.ogTagAddUrl(response.og_url);
        this.metaService.addTag({ property: 'og:type', content: 'website' });
        this.ogTagAddImage(response.og_image);
        this.updateCanonicalUrl(response.og_url);
    }

    setMetaTagsFor404() {
        let url = ''
        if (isPlatformBrowser(this.platformId)) {
            url = window.location.pathname.replace(/^\/+/, '');;
        } else if (isPlatformServer(this.platformId) && this.expressReq) {
            url = `${this.expressReq.path}`.replace(/^\/+/, '');;
        }
        this.metaService.removeTag('name="robots"');
        this.updateTitle('404 Page Not Found');
        this.updateDescription('404 Page Not Found');
        this.ogTagAddDescription();
        this.ogTagAddTitle('404 Page Not Found');
        this.metaService.addTag({ property: 'og:type', content: 'website' });
        this.ogTagAddUrl(url);
        if (this.globalService.branding?.value?.og_fallback_image) {
            this.ogTagAddImage(this.globalService.branding?.value?.og_fallback_image);
        }
        this.updateCanonicalUrl(url);
    }

    /**
     * Update title
     * @param title
     */
    updateTitle(title: string): void {
        if (!title) {
            return;
        }

        this.metaTitleTemplate = title;

        // Skip on the browser during initial hydration: SSR has already
        // populated the correct title, and overwriting it with the raw
        // `{COUNT}`/`{AVG_PRICE}` template causes a regression window
        // crawlers can snapshot.
        if (!this.hydrationComplete) {
            return;
        }

        // Never expose raw placeholders to users: on client-side navigations
        // the stats API call resolves asynchronously, so write a
        // placeholder-free fallback now and let the stats component swap in
        // the real values once the response arrives.
        if (isPlatformBrowser(this.platformId)) {
            title = this.stripMetaPlaceholders(title);
        }

        this.titleService.setTitle(title);
    }

    /**
     * Write a fully-resolved (placeholder-free) title to both `<title>` and
     * `og:title` behind the same hydration guard, so the two can never
     * diverge. Used by the stats components once they swap the real
     * `{COUNT}`/`{AVG_PRICE}` values in: during initial hydration SSR already
     * holds the correct title, so neither tag is touched; afterwards both are
     * updated together.
     * @param title
     */
    updateResolvedTitle(title: string): void {
        if (!title) {
            return;
        }

        this.updateTitle(title);

        // Gate og:title identically to `<title>` above.
        if (!this.hydrationComplete) {
            return;
        }

        this.ogTagAddTitle(title);
    }

    /**
     * Resolve the stored title/description templates against live stats and
     * write the results to `<title>`, `og:title`, `description` and
     * `og:description`. All `{COUNT}`/`{AVG_PRICE}` occurrences are replaced
     * (case-insensitively, via `resolveTemplate`); an empty placeholder value
     * strips the token rather than emitting a literal, so a missing
     * `avg_price` never renders text like `$null`.
     * @param placeholders map of placeholder name (COUNT/AVG_PRICE) to value
     */
    applyStatsPlaceholders(placeholders: Record<string, string>): void {
        const description = this.resolveTemplate(this.metaDescriptionTemplate, placeholders);
        this.metaTagUpdate(description, 'name="description"');
        this.metaTagUpdate(description, 'property="og:description"');

        const title = this.resolveTemplate(this.metaTitleTemplate, placeholders);
        this.updateResolvedTitle(title);
        this.publishHeading(this.resolveTemplate(this.pageHeadingTemplate, placeholders));
    }

    /**
     * Store the page heading template and publish it for the layout. Unlike the meta
     * tags the heading is visible body text, so the tokens are stripped until stats
     * arrive rather than being rendered literally.
     * @param heading the page title as returned by the pages API
     */
    public setPageHeading(heading: string): void {
        const template = heading || '';

        // Already holding a heading for this exact template -- SSR resolved it and
        // handed it over, or the stats pass resolved it here. Republishing the
        // stripped template would drop the numbers, so leave it. Checking the
        // template rather than a hydration flag matters because the page layout is
        // lazily loaded and can mount after that flag has flipped.
        if (template === this.pageHeadingTemplate && this.pageHeading.value) {
            return;
        }

        this.pageHeadingTemplate = template;
        this.publishHeading(this.stripMetaPlaceholders(template));
    }

    /**
     * Publish the heading for the layout, and on the server also hand it to the
     * browser so hydration starts from the same text instead of recomputing it.
     */
    private publishHeading(heading: string): void {
        if (isPlatformServer(this.platformId)) {
            this.transferState.set(PAGE_HEADING_KEY, {
                template: this.pageHeadingTemplate,
                heading,
            });
        }

        this.pageHeading.next(heading);
    }

    /**
     * Update description
     * @param description
     */
    public updateDescription(description: string): void {
        if (!description) {
            return;
        }

        this.metaDescriptionTemplate = description;

        // Skip on the browser during initial hydration: SSR has already
        // populated the correct description, and overwriting it with the
        // raw `{COUNT}`/`{AVG_PRICE}` template causes a regression window
        // crawlers can snapshot. In-app navigations (after the first
        // render) update the description normally.
        if (!this.hydrationComplete) {
            return;
        }

        if (isPlatformBrowser(this.platformId)) {
            description = this.stripMetaPlaceholders(description);
        }

        this.metaService.updateTag({
            name: 'description',
            content: description,
        });
    }

    /**
     * Removes {COUNT}/{AVG_PRICE} placeholders from a meta value so the
     * placeholder text is never shown before the stats response arrives.
     */
    private stripMetaPlaceholders(value: string): string {
        return value
            .replace(/\{COUNT\}/g, '')
            .replace(/\{AVG_PRICE\}/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
    }

    /**
     * Update keywords
     * @param keywords
     */
    public updateKeywords(keywords: string): void {
        if (keywords) {
            this.metaService.updateTag({
                name: 'keywords',
                content: keywords,
            });
        }
    }

    metaTagFetch() {
        const metaContent = this.metaService.getTag('name="description"')?.content;
        return metaContent;
    }

    /**
     * Fetch the stored meta description template (may contain {COUNT}/{AVG_PRICE} placeholders)
     * @returns the stored meta description template
     */
    metaDescriptionFetch() {
        return this.metaDescriptionTemplate;
    }

    /**
     * Whether any stored template still needs the stats response. The heading, meta
     * title and meta description are edited independently, so a token in any one of
     * them means the stats call has to run.
     */
    public hasPendingStatsTokens(): boolean {
        return [this.pageHeadingTemplate, this.metaTitleTemplate, this.metaDescriptionTemplate].some(
            (template) => /\{(COUNT|AVG_PRICE)\}/i.test(template),
        );
    }

    metaTagUpdate(content: string | undefined, selector: string) {
        if (content) {
            this.metaService.updateTag({ content: content }, selector);
        }
    }

    ogTagAddDescription() {
        const ogDesc = this.metaService.getTag('name="description"')?.content;
        if (ogDesc) {
            this.metaService.updateTag({ property: 'og:description', content: ogDesc });
        }
    }

    ogTagAddTitle(title: string | undefined) {
        if (title) {
            this.metaService.updateTag({ property: 'og:title', content: title });
        }
    }

    ogTagAddUrl(url: string) {
        const canonicalUrl = this.constructCanonicalUrl(url);

        if (canonicalUrl) {
            this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
        }
    }

    ogTagAddImage(image: string | undefined) {
        if (image) {
            try {
                image = encodeURI(decodeURI(image));
            } catch {
                image = encodeURI(image);
            }

            this.metaService.updateTag({ property: 'og:image:width', content: '300' });
            this.metaService.updateTag({ property: 'og:image:height', content: '200' });
            this.metaService.updateTag({ property: 'og:image', content: image });
        }
    }

    /**
     * Update canonical url
     * @param path
     * @returns
     */
    updateCanonicalUrl(path: string) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const canonicalUrl = this.constructCanonicalUrl(path);
        if (canonicalUrl) {
            const existingCanonical = document.querySelector('link[rel="canonical"]');
            if (existingCanonical) {
                existingCanonical.setAttribute('href', canonicalUrl);
            } else {
                const link: HTMLLinkElement = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                link.setAttribute('href', canonicalUrl);
                document.head.appendChild(link);
            }
        }
    }

    /**
     * To update the schema markup
     * @param property
     * @param path
     * @returns
     */
    updateSchemaMarkup(property: Property, path: string) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const canonicalUrl = this.constructCanonicalUrl(path);
        const schemaMarkupContent = CommonHelper.constructSchemaMarkup(property, canonicalUrl, this.globalService.branding?.value?.fallback_image!);
        const schemaMarkup = document.querySelector('#property-schema');
        if (schemaMarkup) {
            schemaMarkup.textContent = JSON.stringify(schemaMarkupContent);
        } else {
            const script: HTMLScriptElement = document.createElement('script');
            script.id = 'property-schema';
            script.setAttribute('type', 'application/ld+json');
            script.textContent = JSON.stringify(schemaMarkupContent);
            document.head.appendChild(script);
        }
    }

    /**
     * To construct canonical url
     * @param path
     * @returns
     */
    private constructCanonicalUrl(path: string): string {
        // On the server the canonical link is injected by server.ts from the raw
        // request URL, so mirror those inputs here — same host, same path — and
        // og:url comes out byte-identical to the canonical tag beside it.
        const browser = isPlatformBrowser(this.platformId);
        const host = browser ? window.location.host : (this.expressReq?.headers?.host ?? '');
        const target = browser ? path : (this.expressReq?.originalUrl ?? path);

        if (!host) {
            return '';
        }

        return CommonHelper.buildCanonicalUrl(host, target);
    }

    /**
     * Angular keeps query params off `route.url` — it is a `UrlSegment[]` of path
     * segments only, with the query living on `route.queryParams`. Joining those
     * segments therefore drops `?page=N`, so the canonical written during hydration
     * would overwrite SSR's correct `/city?page=2` with a bare `/city` — telling
     * Google that page 2 is really page 1.
     *
     * Re-attaching `page` keeps the browser in step with the server, which builds
     * from `req.originalUrl` and never lost it. Only `page` is carried across, so
     * og:url stays free of tracking params.
     */
    private pathWithPageParam(route: ActivatedRouteSnapshot): string {
        const path = route.url.map((segment) => segment.path).join('/');
        const page = route.queryParams?.['page'];
        return page ? `${path}?page=${page}` : path;
    }

    /**
     * Resolvers are where the canonical normally gets set, but Angular only re-runs
     * them when path params change — `runGuardsAndResolvers` defaults to
     * 'paramsChange', and only the static-page route opts into 'always'. Paging
     * inside a search route is a query-only navigation, so the resolver never fires
     * and the tag keeps the previous value: click from /homes to ?page=2 and the
     * canonical still reads /homes.
     *
     * The canonical is a pure function of the URL, so it is safe to recompute from
     * `location` here. Only do that when the tag already points at this same path —
     * that means a resolver set it for the page we are on and we are just refreshing
     * the page param, rather than stomping on a canonical another page owns.
     */
    private refreshCanonicalForQueryChange() {
        const existing = document.querySelector('link[rel="canonical"]');
        if (!existing) {
            return;
        }

        const currentHref = existing.getAttribute('href') || '';
        const target = CommonHelper.buildCanonicalUrl(
            window.location.host,
            window.location.pathname + window.location.search,
        );

        try {
            if (new URL(currentHref).pathname !== new URL(target).pathname) {
                return; // Different page — leave it to that route's resolver.
            }
        } catch {
            return; // Unparseable existing tag; don't guess.
        }

        if (currentHref !== target) {
            existing.setAttribute('href', target);
            this.metaService.updateTag({ property: 'og:url', content: target });
        }
    }
}
